import { VenueBasedMembershipGroupSerializer } from "@app-contexts/dashboard/features/memberships/data/types/membership-groups";
import { NestedPublicVenueSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import StripeAffirmText from "@app-contexts/public/features/tickets/components/StripeAffirmText/StripeAffirmText.web";
import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { X_SP_UUID } from "@shared/env";
import useDebounce from "@shared/hooks/useDebounce";
import useAnalytics from "@shared/lib/analytics/hooks/useAnalytics";
import UserInteractionLogger from "@shared/lib/analytics/internal/UserInteractionLogger";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { trackItemsInCart } from "@shared/modules/events/utils/detail-page-helpers";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import {
  PublicMembershipGroupSerializer,
  PublicMembershipLevelSerializer,
} from "@shared/modules/memberships/types";
import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";
import ItemQuantitySelectInfo from "@ui-application/data-display/ItemQuantitySelectInfo/ItemQuantitySelectInfo.web";
import DetailCardContainer from "@ui-kit/data-display/DetailCardContainer/DetailCardContainer.web";
import Box from "@ui-kit/layout/Box/Box.web";
import ReadMore from "@ui-kit/typography/ReadMore/ReadMore.web";
import { useTranslation } from "@shared/utils/internationalization";
import { useEffect, useMemo, useRef, useState } from "react";
import { isPublicMembershipGroup } from "../../utils/helpers";

export type MembershipTypesContainerType = {
  selectedAttractionMembership:
    | PublicMembershipGroupSerializer
    | VenueBasedMembershipGroupSerializer;
  membershipLevel: PublicMembershipLevelSerializer;
  venue: NestedPublicVenueSerializer;
  hasAllInPricing: boolean;
  isLoading: boolean;
  isModal?: boolean;
  dynamicHoldSlug?: string;
  purchaseLimits?: {
    min: number;
    max: number;
  };
  /**
   * On mount, the amount requested is used to diplay the quantity.
   * By default, this component looks at the basket to determine the amount requested.
   * This parameter overrides that and explicitly sets the amount.
   */
  initialAmountRequested?: number;
  /**
   * By default, this component performs a basket update after the quantity change debounce mechanism completes.
   * This parameter overrides that mechanism *completely* (including post-add to cart logic, like analytics).
   */
  onAmountRequested?: (
    ml: PublicMembershipLevelSerializer,
    amountRequested: number
  ) => void;
  forceQuantityButtons?: boolean;
  withDebounce?: boolean;
  enableTracking?: boolean;
  enableAffirm?: boolean;
  publishableApiKey?: string;
  accessGrantedAccountId?: string | null;
};

export default function MembershipTypesContainer({
  selectedAttractionMembership,
  membershipLevel,
  venue,
  hasAllInPricing,
  isLoading,
  isModal = false,
  dynamicHoldSlug = undefined,
  purchaseLimits = undefined,
  initialAmountRequested = undefined,
  onAmountRequested,
  forceQuantityButtons = false,
  withDebounce = true,
  enableTracking = true,
  enableAffirm = true,
  publishableApiKey,
  accessGrantedAccountId,
}: MembershipTypesContainerType) {
  const analytics = useAnalytics();
  const { t } = useTranslation("common");
  const { modifyItems, itemGroups, isExpired, purchaseSourcePlatform } =
    useBasketService();

  const membershipLevelsInBasket = useMemo(
    () =>
      itemGroups.filter(
        ({ membership_level }) => membership_level === membershipLevel.id
      ),
    [itemGroups, membershipLevel.id]
  );

  const LevelsInCartReducer = (
    curr: number,
    itemGroup: TicketItemGroupType
  ) => {
    if (itemGroup?.quantity) {
      return curr + itemGroup.quantity;
    }
    return curr + 1;
  };

  // This controls the quantity amount requested to the API, changed by this component only.
  const [amountRequested, setAmountRequested] = useState({
    amount:
      initialAmountRequested ??
      membershipLevelsInBasket?.reduce(LevelsInCartReducer, 0) ??
      0,
  });

  const [debouncedAmountRequested] = useDebounce(amountRequested, 500);

  // This controls the quantity amount displayed to the user, changed by this component and by the basket.
  const [amountSelectedDisplay, setAmountSelectedDisplay] = useState(
    membershipLevelsInBasket?.reduce(LevelsInCartReducer, 0) ?? 0
  );

  const componentDidMount = useRef(false);
  const allowRequest = useRef(true);

  useEffect(() => {
    // Change the displayed quantity when the basket quantity changes.
    if (membershipLevelsInBasket?.length) {
      setAmountSelectedDisplay(
        membershipLevelsInBasket?.reduce(LevelsInCartReducer, 0) ?? 0
      );
    }
    // eslint-disable-next-line
  }, [membershipLevelsInBasket?.length]); // if membershipLevelsInBasket is included directly, it causes an infinite loop

  useEffect(() => {
    // Change the displayed quantity when this component requests a change.
    if (allowRequest.current) {
      setAmountSelectedDisplay(amountRequested.amount ?? 0);
    }
  }, [amountRequested]);

  useEffect(() => {
    if (typeof onAmountRequested === "function" && !withDebounce) {
      onAmountRequested(membershipLevel, amountRequested.amount);
    }
  }, [amountRequested.amount]);

  useEffect(() => {
    if (!componentDidMount.current) {
      // do nothing on the first component render
      componentDidMount.current = true;
      return;
    }

    // if an `onAmountRequested` function is passed, override remaining logic
    if (typeof onAmountRequested === "function") {
      // have to nest this check to prevent adding to cart
      if (withDebounce) {
        onAmountRequested(membershipLevel, debouncedAmountRequested.amount);
      }
      return;
    }

    // prevent this api call from firing automatically when the cart expires and item amount is reset to 0
    if (allowRequest.current) {
      modifyItems(
        {
          item: {
            membership_level: membershipLevel.id,
          },
          quantity: debouncedAmountRequested.amount,
        },
        { hold_slug: dynamicHoldSlug }
      )
        .then((basket) => {
          if (!basket) {
            return;
          }

          try {
            if (enableTracking) {
              const spAddToCartCall = () => {
                logAppInfo("spAddToCartCall");
                UserInteractionLogger.addToCart({
                  item: {
                    item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id,
                    type: membershipLevel.id,
                  },
                  count: debouncedAmountRequested.amount,
                  venue: venue.name,
                  basket,
                  membershipGroup: selectedAttractionMembership,
                });
              };
              logAppInfo(`calling trackItemsInCart ${basket?.id}`);
              trackItemsInCart(
                debouncedAmountRequested.amount,
                basket,
                {
                  itemId: membershipLevel.id,
                  itemName: selectedAttractionMembership.name,
                  itemVariant: membershipLevel.name,
                  itemType: enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id,
                },
                venue,
                analytics,
                spAddToCartCall,
                {
                  uuid: basket[X_SP_UUID],
                }
              );
            }
          } catch (e) {
            logAppInfo(`Returned basket truthiness: ${Boolean(basket)}`);
            recordAppError(e);
          }
        })
        .catch((err) => {
          // On error, revert displayed amount to basket amount
          allowRequest.current = false;
          setAmountSelectedDisplay(membershipLevelsInBasket?.length ?? 0);
          setAmountRequested({ amount: membershipLevelsInBasket?.length ?? 0 });

          logAppInfo("Error changing membership quantity");
          recordAppError(err);
        });
    } else {
      allowRequest.current = true;
    }
  }, [debouncedAmountRequested]); //eslint-disable-line

  // set amount on ticket card to 0 when cart expires
  // re-initialize cart
  useEffect(() => {
    if (isExpired && !onAmountRequested) {
      allowRequest.current = false;
      setAmountRequested({ amount: 0 });
    }
  }, [isExpired]);

  const getAffirmItemPrice = (): string => {
    const basePrice = membershipLevel.price?.toString() ?? "0";
    if (
      hasAllInPricing &&
      membershipLevel.fees_pricing_info &&
      membershipLevel.fees_pricing_info[purchaseSourcePlatform]
    ) {
      return (
        membershipLevel.fees_pricing_info[purchaseSourcePlatform][
          PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id
        ]?.total_price?.toString() ?? basePrice
      );
    }
    return basePrice;
  };

  const affirmItemPrice = getAffirmItemPrice();

  const isAffirmMessageEnabled =
    enableAffirm &&
    Boolean(publishableApiKey) &&
    venue?.affirm_is_enabled &&
    Number(affirmItemPrice) >= PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.minAmount &&
    Number(affirmItemPrice) <= PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.maxAmount;

  const handleAmountSelected = (amount: number) => {
    setAmountRequested({ amount });
  };

  return (
    <DetailCardContainer key={membershipLevel.id} isModal={isModal}>
      <ItemQuantitySelectInfo
        itemTitle={membershipLevel.name}
        itemPrice={membershipLevel.price}
        currency={venue.currency}
        feesPricingInfo={membershipLevel.fees_pricing_info}
        hasAllInPricing={hasAllInPricing}
        showQuantityButtons={!membershipLevel.sold_out}
        soldOut={membershipLevel.sold_out}
        minSelectionAmount={purchaseLimits?.min}
        maxSelectionAmount={
          purchaseLimits?.max || membershipLevel.purchase_limit
        }
        amountSelected={amountSelectedDisplay}
        onAmountSelectedChange={handleAmountSelected}
        isLoading={isLoading}
        hasAssignedSeating={Boolean(
          selectedAttractionMembership.reserved_seating_benefits &&
            selectedAttractionMembership.reserved_seating_benefits.length > 0
        )}
        frontendDetailsUrl={
          isPublicMembershipGroup(selectedAttractionMembership)
            ? selectedAttractionMembership.frontend_details_url
            : selectedAttractionMembership.slug_custom ??
              selectedAttractionMembership.slug
        }
        forceQuantityButtons={forceQuantityButtons}
      />
      {isAffirmMessageEnabled && !membershipLevel.sold_out && (
        <StripeAffirmText
          publishableApiKey={publishableApiKey ?? ""}
          accessGrantedAccountId={accessGrantedAccountId ?? ""}
          itemPrice={affirmItemPrice}
          currency={enums.CURRENCY_MAP.USD.key}
          isSoldOut={Boolean(membershipLevel.sold_out)}
        />
      )}
      {!isLoading && membershipLevel.description && (
        <>
          <Box
            hidden={!membershipLevel.description || isModal}
            minHeight="element.lg"
            mt="none"
          />
          <ReadMore
            descriptionHtml={membershipLevel.description}
            openReadMoreText={t("Read More")}
            closeReadMoreText={t("Show Less")}
          />
        </>
      )}
    </DetailCardContainer>
  );
}
